import { openDB, DBSchema, IDBPDatabase } from 'idb';
import {
  PrinterRoutingConfig,
  CategoryPrinterMapping,
  PrinterRoutingMode,
  AndroidPrinter,
  RoutedPrintPayload,
  PrinterStatusUpdate,
  PrinterTicketPayload,
  PaymentMethodFilter,
} from '../types/printerRouting';
import { sendToAndroidPrint } from '../utils/androidPrint';

interface PrinterRoutingDB extends DBSchema {
  routing_config: {
    key: string;
    value: PrinterRoutingConfig;
  };
  printer_cache: {
    key: string;
    value: AndroidPrinter;
  };
}

const DB_NAME = 'printer-routing-db';
const CONFIG_STORE = 'routing_config';
const PRINTER_STORE = 'printer_cache';
const DB_VERSION = 1;
const CONFIG_ID = 'default-routing-config';

let dbInstance: IDBPDatabase<PrinterRoutingDB> | null = null;

const initDB = async (): Promise<IDBPDatabase<PrinterRoutingDB>> => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<PrinterRoutingDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(CONFIG_STORE)) {
        db.createObjectStore(CONFIG_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PRINTER_STORE)) {
        db.createObjectStore(PRINTER_STORE, { keyPath: 'id' });
      }
    },
  });

  const config = await dbInstance.get(CONFIG_STORE, CONFIG_ID);
  if (!config) {
    await initializeDefaultConfig(dbInstance);
  }

  return dbInstance;
};

const initializeDefaultConfig = async (db: IDBPDatabase<PrinterRoutingDB>) => {
  const defaultConfig: PrinterRoutingConfig = {
    id: CONFIG_ID,
    mode: 'classic',
    enabled: false,
    mainPrinterPaymentFilter: 'all',
    categoryMappings: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  await db.put(CONFIG_STORE, defaultConfig);
};

export const getRoutingConfig = async (): Promise<PrinterRoutingConfig> => {
  const db = await initDB();
  let config = await db.get(CONFIG_STORE, CONFIG_ID);

  if (!config) {
    await initializeDefaultConfig(db);
    config = (await db.get(CONFIG_STORE, CONFIG_ID))!;
  }

  // Migration: ajouter mainPrinterPaymentFilter si manquant
  if (!config.mainPrinterPaymentFilter) {
    config.mainPrinterPaymentFilter = 'all';
    await db.put(CONFIG_STORE, config);
  }

  // Migration: ajouter paymentFilter aux mappings si manquant
  let needsUpdate = false;
  config.categoryMappings = config.categoryMappings.map(mapping => {
    if (!mapping.paymentFilter) {
      needsUpdate = true;
      return { ...mapping, paymentFilter: 'all' as const };
    }
    return mapping;
  });

  if (needsUpdate) {
    await db.put(CONFIG_STORE, config);
  }

  return config;
};

export const updateRoutingConfig = async (
  updates: Partial<Omit<PrinterRoutingConfig, 'id' | 'created_at'>>
): Promise<void> => {
  const db = await initDB();
  const config = await getRoutingConfig();

  const updatedConfig: PrinterRoutingConfig = {
    ...config,
    ...updates,
    id: CONFIG_ID,
    updated_at: new Date().toISOString(),
  };

  await db.put(CONFIG_STORE, updatedConfig);
};

export const setRoutingMode = async (mode: PrinterRoutingMode): Promise<void> => {
  await updateRoutingConfig({ mode });
};

export const toggleRoutingEnabled = async (enabled: boolean): Promise<void> => {
  await updateRoutingConfig({ enabled });
};

export const setMainPrinter = async (printerId: string): Promise<void> => {
  await updateRoutingConfig({ mainPrinterId: printerId });
};

export const setMainPrinterPaymentFilter = async (filter: PaymentMethodFilter): Promise<void> => {
  await updateRoutingConfig({ mainPrinterPaymentFilter: filter });
};

export const addCategoryMapping = async (
  categoryId: string,
  categoryName: string,
  printerId: string,
  printerName: string,
  paymentFilter: PaymentMethodFilter = 'all'
): Promise<void> => {
  const config = await getRoutingConfig();

  const existingIndex = config.categoryMappings.findIndex(
    m => m.categoryId === categoryId
  );

  const newMapping: CategoryPrinterMapping = {
    categoryId,
    categoryName,
    printerId,
    printerName,
    paymentFilter,
    // v5 multi-imprimantes : préserver le profileId déjà choisi pour cette catégorie
    profileId: existingIndex >= 0 ? config.categoryMappings[existingIndex].profileId : undefined,
  };

  if (existingIndex >= 0) {
    config.categoryMappings[existingIndex] = newMapping;
  } else {
    config.categoryMappings.push(newMapping);
  }

  await updateRoutingConfig({ categoryMappings: config.categoryMappings });
};

/**
 * v5 multi-imprimantes : définit le PROFIL (module) d'une imprimante cuisine (par catégorie).
 * profileId undefined → repli sur le profil actif global.
 */
export const setCategoryPrinterProfile = async (
  categoryId: string,
  profileId: string | undefined
): Promise<void> => {
  const config = await getRoutingConfig();
  const idx = config.categoryMappings.findIndex(m => m.categoryId === categoryId);
  if (idx >= 0) {
    config.categoryMappings[idx] = { ...config.categoryMappings[idx], profileId };
    await updateRoutingConfig({ categoryMappings: config.categoryMappings });
  }
};

/**
 * v5 multi-imprimantes : définit le PROFIL (module) de l'imprimante CLIENT (principale).
 * profileId undefined → repli sur le profil actif global.
 */
export const setMainPrinterProfile = async (
  profileId: string | undefined
): Promise<void> => {
  await updateRoutingConfig({ mainPrinterProfileId: profileId });
};

export const updateCategoryPaymentFilter = async (
  categoryId: string,
  paymentFilter: PaymentMethodFilter
): Promise<void> => {
  const config = await getRoutingConfig();
  const mapping = config.categoryMappings.find(m => m.categoryId === categoryId);

  if (mapping) {
    mapping.paymentFilter = paymentFilter;
    await updateRoutingConfig({ categoryMappings: config.categoryMappings });
  }
};

export const removeCategoryMapping = async (categoryId: string): Promise<void> => {
  const config = await getRoutingConfig();
  const categoryMappings = config.categoryMappings.filter(
    m => m.categoryId !== categoryId
  );
  await updateRoutingConfig({ categoryMappings });
};

export const clearAllMappings = async (): Promise<void> => {
  await updateRoutingConfig({ categoryMappings: [] });
};

export const cachePrinters = async (printers: AndroidPrinter[]): Promise<void> => {
  const db = await initDB();
  const tx = db.transaction(PRINTER_STORE, 'readwrite');

  await tx.store.clear();

  for (const printer of printers) {
    await tx.store.put(printer);
  }

  await tx.done;
};

export const getCachedPrinters = async (): Promise<AndroidPrinter[]> => {
  const db = await initDB();
  return await db.getAll(PRINTER_STORE);
};

export const getPrintersFromAndroid = async (): Promise<AndroidPrinter[]> => {
  if (!window.android?.getPrinterList) {
    console.warn('Android bridge not available for printer list');
    return [];
  }

  try {
    console.log('[getPrintersFromAndroid] Calling window.android.getPrinterList()...');
    const rawResponse = await window.android.getPrinterList();

    console.log('[getPrintersFromAndroid] Réponse brute getPrinterList:', rawResponse);
    console.log('[getPrintersFromAndroid] Type de la réponse:', typeof rawResponse);
    console.log('[getPrintersFromAndroid] Is Array?:', Array.isArray(rawResponse));

    let printers: AndroidPrinter[];

    if (typeof rawResponse === 'string') {
      console.log('[getPrintersFromAndroid] Parsing JSON string...');
      printers = JSON.parse(rawResponse);
      console.log('[getPrintersFromAndroid] Printers après parsing:', printers);
    } else if (Array.isArray(rawResponse)) {
      console.log('[getPrintersFromAndroid] Already an array, using directly');
      printers = rawResponse;
    } else {
      console.error('[getPrintersFromAndroid] Unexpected response type:', rawResponse);
      return [];
    }

    console.log('[getPrintersFromAndroid] Final printers count:', printers.length);
    console.log('[getPrintersFromAndroid] Printers:', printers);

    await cachePrinters(printers);
    return printers;
  } catch (error) {
    console.error('[getPrintersFromAndroid] Error getting printers from Android:', error);
    console.error('[getPrintersFromAndroid] Error stack:', error instanceof Error ? error.stack : 'No stack');
    return await getCachedPrinters();
  }
};

export const testPrinterConnection = async (
  printerId: string,
  testContent: string
): Promise<boolean> => {
  if (!window.android?.testPrinter) {
    console.warn('Android bridge not available for printer test');
    return false;
  }

  try {
    return await window.android.testPrinter(printerId, testContent);
  } catch (error) {
    console.error('Error testing printer:', error);
    return false;
  }
};

export const sendRoutedPrintJob = async (
  payload: RoutedPrintPayload
): Promise<PrinterStatusUpdate[]> => {
  const isAndroid = !!(window.android?.print);
  console.log(`[sendRoutedPrintJob] 🖨️ Détection automatique: ${isAndroid ? 'Android Bridge' : 'Windows/Browser'}`);

  const results: PrinterStatusUpdate[] = [];
  const PRINT_DELAY_MS = 800; // Délai entre chaque impression (0.8 secondes)

  // Fonction d'impression Windows via window.print()
  const printWithBrowser = (content: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        const doc = iframe.contentDocument;
        if (!doc) {
          throw new Error('Impossible de créer le document d\'impression');
        }

        doc.open();
        doc.write(`
          <html>
            <head>
              <title>Ticket</title>
              <style>
                body {
                  font-family: 'Courier New', monospace;
                  white-space: pre-wrap;
                  font-size: 12px;
                  margin: 10px;
                }
              </style>
            </head>
            <body>${content}</body>
          </html>
        `);
        doc.close();

        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();

        setTimeout(() => {
          document.body.removeChild(iframe);
          resolve();
        }, 100);
      } catch (error) {
        reject(error);
      }
    });
  };

  try {
    console.log('[sendRoutedPrintJob] ========================================');
    console.log('[sendRoutedPrintJob] Mode:', payload.mode);
    console.log('[sendRoutedPrintJob] Number of tickets:', payload.tickets.length);
    console.log('[sendRoutedPrintJob] ========================================');

    // Imprimer chaque ticket successivement avec un délai entre chaque
    for (let i = 0; i < payload.tickets.length; i++) {
      const ticket = payload.tickets[i];
      const ticketNum = i + 1;

      console.log(`[sendRoutedPrintJob] 📄 Impression ${ticketNum}/${payload.tickets.length}:`, {
        printerName: ticket.printerName,
        isPrimary: ticket.isPrimary,
        categoryName: ticket.categoryName || 'Ticket complet',
        contentLength: ticket.content.length
      });

      try {
        if (isAndroid) {
          // Impression Android (découpage automatique des gros tickets image)
          sendToAndroidPrint(ticket.content);
          console.log(`[sendRoutedPrintJob] ✅ Ticket ${ticketNum} envoyé via Android: ${ticket.printerName}`);
        } else {
          // Impression Windows/Browser
          await printWithBrowser(ticket.content);
          console.log(`[sendRoutedPrintJob] ✅ Ticket ${ticketNum} envoyé via Windows: ${ticket.printerName}`);
        }

        results.push({
          printerId: ticket.printerId,
          success: true,
          timestamp: new Date().toISOString()
        });

        // Délai entre chaque impression (sauf pour le dernier)
        if (i < payload.tickets.length - 1) {
          console.log(`[sendRoutedPrintJob] ⏳ Délai de ${PRINT_DELAY_MS}ms avant impression suivante...`);
          await new Promise(resolve => setTimeout(resolve, PRINT_DELAY_MS));
        }

      } catch (error) {
        console.error(`[sendRoutedPrintJob] ❌ Erreur impression ${ticketNum}:`, error);

        results.push({
          printerId: ticket.printerId,
          success: false,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString()
        });
      }
    }

    console.log('[sendRoutedPrintJob] ========================================');
    console.log('[sendRoutedPrintJob] ✅ Impression terminée:', {
      total: payload.tickets.length,
      success: results.filter(r => r.success).length,
      failed: results.filter(r => !r.success).length
    });
    console.log('[sendRoutedPrintJob] ========================================');

    return results;
  } catch (error) {
    console.error('[sendRoutedPrintJob] Error sending routed print job:', error);
    console.error('[sendRoutedPrintJob] Error stack:', error instanceof Error ? error.stack : 'No stack');

    // Generate error results for all printers
    const errorResults: PrinterStatusUpdate[] = payload.tickets.map(ticket => ({
      printerId: ticket.printerId,
      success: false,
      error: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString()
    }));

    return errorResults;
  }
};

export const buildRoutedPayload = (
  orderNumber: string,
  orderDate: string,
  orderType: 'takeaway' | 'dine-in',
  tickets: PrinterTicketPayload[],
  tableNumber?: string,
  customerName?: string
): RoutedPrintPayload => {
  const config = getRoutingConfig();

  return {
    orderNumber,
    orderDate,
    orderType,
    tableNumber,
    customerName,
    mode: 'classic',
    tickets,
  };
};

export const getPrinterForCategory = async (
  categoryId: string
): Promise<CategoryPrinterMapping | undefined> => {
  const config = await getRoutingConfig();
  return config.categoryMappings.find(m => m.categoryId === categoryId);
};

export const validateRoutingConfig = async (): Promise<{
  valid: boolean;
  errors: string[];
}> => {
  const config = await getRoutingConfig();
  const errors: string[] = [];

  if (!config.enabled) {
    return { valid: true, errors: [] };
  }

  if (config.mode === 'hybrid' && !config.mainPrinterId) {
    errors.push('Mode Hybride nécessite une imprimante principale');
  }

  if (config.mode !== 'classic' && config.categoryMappings.length === 0) {
    errors.push('Aucune catégorie mappée aux imprimantes');
  }

  const printers = await getCachedPrinters();
  const printerIds = new Set(printers.map(p => p.id));

  if (config.mainPrinterId && !printerIds.has(config.mainPrinterId)) {
    errors.push('Imprimante principale non disponible');
  }

  for (const mapping of config.categoryMappings) {
    if (!printerIds.has(mapping.printerId)) {
      errors.push(`Imprimante pour ${mapping.categoryName} non disponible`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};
